import React, { useState, useEffect } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Button,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  FileCopy as DuplicateIcon,
  DragHandle as DragHandleIcon,
  Web as WebIcon,
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  Settings as SettingsIcon,
  List as ListIcon,
  Article as ArticleIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import useNotification from '../../hooks/useNotification';

const WireframeAccordion = ({ projectId, conversation, isStreaming, streamingContent, onPageUpdate, showConversation, onNotification }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedPage, setExpandedPage] = useState(null);
  const [createPageOpen, setCreatePageOpen] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [updatingPages, setUpdatingPages] = useState(new Set());
  const [autoGenerating, setAutoGenerating] = useState(false);
  
  // Create page form state
  const [newPage, setNewPage] = useState({
    page_id: '',
    title: '',
    page_type: 'other',
    description: ''
  });

  const { showNotification } = useNotification();

  // Page type options with icons
  const pageTypes = [
    { value: 'authentication', label: 'Authentication', icon: PersonIcon },
    { value: 'dashboard', label: 'Dashboard', icon: DashboardIcon },
    { value: 'profile', label: 'Profile', icon: PersonIcon },
    { value: 'settings', label: 'Settings', icon: SettingsIcon },
    { value: 'list', label: 'List View', icon: ListIcon },
    { value: 'detail', label: 'Detail View', icon: ArticleIcon },
    { value: 'form', label: 'Form', icon: EditIcon },
    { value: 'landing', label: 'Landing Page', icon: HomeIcon },
    { value: 'other', label: 'Other', icon: WebIcon }
  ];

  // Load pages on mount and auto-generate from conversation
  useEffect(() => {
    loadWireframePages();
  }, [projectId]);

  // Auto-generate pages from conversation content
  useEffect(() => {
    if (conversation && pages.length === 0 && !autoGenerating && !loading) {
      autoGenerateFromConversation();
    }
  }, [conversation, pages.length, autoGenerating, loading]);

  const loadWireframePages = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/wireframes`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to load wireframes: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        console.log('📄 Loaded wireframe pages:', {
          totalPages: data.data.pages?.length || 0,
          pages: data.data.pages?.map(page => ({
            id: page.page_id,
            title: page.title,
            hasContent: !!page.html_content,
            contentLength: page.html_content?.length || 0,
            status: page.status
          }))
        });
        
        setPages(data.data.pages || []);
        // Auto-expand first page if no pages expanded
        if (data.data.pages.length > 0 && !expandedPage) {
          setExpandedPage(data.data.pages[0].page_id);
        }
      }
    } catch (error) {
      console.error('Error loading wireframe pages:', error);
      showNotification({
        message: `Failed to load wireframe pages: ${error.message}`,
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Debug function to inspect page content
  const debugPageContent = (page) => {
    console.log('🔍 Page content debug:', {
      pageId: page.page_id,
      title: page.title,
      hasHtmlContent: !!page.html_content,
      htmlContentLength: page.html_content?.length || 0,
      htmlContentPreview: page.html_content?.substring(0, 200) + '...',
      status: page.status
    });
  };

  const autoGenerateFromConversation = async () => {
    try {
      setAutoGenerating(true);

      // Extract wireframe HTML from conversation messages
      const assistantMessages = conversation?.messages?.filter(msg => msg.role === 'assistant') || [];
      
      // Try to find the message with the most HTML content
      let bestContent = '';
      let bestContentScore = 0;
      
      for (const message of assistantMessages) {
        if (message.content) {
          const htmlTagCount = (message.content.match(/<[^>]+>/g) || []).length;
          const hasHtmlDoc = message.content.includes('<!DOCTYPE') || message.content.includes('<html');
          const score = htmlTagCount + (hasHtmlDoc ? 1000 : 0);
          
          if (score > bestContentScore) {
            bestContent = message.content;
            bestContentScore = score;
          }
        }
      }

      // Use streaming content if available and better, otherwise use best found content
      const content = (isStreaming && streamingContent) ? streamingContent : (bestContent || assistantMessages[assistantMessages.length - 1]?.content || '');

      console.log('🔍 Auto-generation content analysis:', {
        hasContent: !!content,
        contentLength: content?.length || 0,
        hasHtml: content?.toLowerCase()?.includes('html') || content?.includes('<'),
        contentPreview: content?.substring(0, 500) + '...',
        fullContent: content // Show full content for debugging
      });
      
      console.log('🔍 Conversation structure:', {
        totalMessages: conversation?.messages?.length || 0,
        assistantMessages: conversation?.messages?.filter(msg => msg.role === 'assistant')?.length || 0,
        latestMessageRole: conversation?.messages?.length > 0 ? conversation.messages[conversation.messages.length - 1].role : 'none',
        latestMessagePreview: conversation?.messages?.length > 0 ? conversation.messages[conversation.messages.length - 1].content?.substring(0, 200) + '...' : 'none'
      });

      if (!content || (!content.toLowerCase().includes('html') && !content.includes('<'))) {
        console.log('No wireframe content found in conversation - content must contain HTML');
        setAutoGenerating(false);
        return;
      }

      // Call backend to auto-generate pages from conversation content
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/auto-generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          conversation_content: content,
          conversation_id: conversation.id,
          auto_parse: true,
          refresh_existing: pages.length > 0
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to auto-generate pages: ${response.status}`);
      }

      const data = await response.json();
      if (data.success && data.data.pages && data.data.pages.length > 0) {
        setPages(data.data.pages);
        setExpandedPage(data.data.pages[0].page_id);
        
        const notification = onNotification || showNotification;
        notification({
          message: `Auto-generated ${data.data.pages.length} page${data.data.pages.length !== 1 ? 's' : ''} from conversation!`,
          type: 'success'
        });

        console.log('🎯 Auto-Generated Pages:', {
          pages: data.data.pages.length,
          generation_info: data.data.generation_info
        });
      } else {
        console.log('No pages could be auto-generated from conversation');
      }
    } catch (error) {
      console.error('Error auto-generating pages:', error);
      const notification = onNotification || showNotification;
      notification({
        message: `Could not auto-generate pages: ${error.message}`,
        type: 'warning'
      });
    } finally {
      setAutoGenerating(false);
    }
  };

  const debugWireframePages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/debug`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to debug wireframes: ${response.status}`);
      }

      const data = await response.json();
      console.log('🐛 Debug wireframe pages:', data.data);
      
      const notification = onNotification || showNotification;
      notification({
        message: `Debug info logged to console. ${data.data.total_pages} pages found.`,
        type: 'info'
      });
    } catch (error) {
      console.error('Error debugging wireframes:', error);
      const notification = onNotification || showNotification;
      notification({
        message: `Debug failed: ${error.message}`,
        type: 'error'
      });
    }
  };

  const createWireframePage = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(newPage)
      });

      if (!response.ok) {
        throw new Error(`Failed to create page: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setPages(prev => [...prev, data.data.page]);
        setExpandedPage(data.data.page.page_id);
        setCreatePageOpen(false);
        setNewPage({ page_id: '', title: '', page_type: 'other', description: '' });
        
        showNotification({
          message: `Created ${data.data.page.title} page successfully!`,
          type: 'success'
        });

        // Show generation info if available
        if (data.data.generation_info) {
          const { ai_generated, token_usage, fallback_used } = data.data.generation_info;
          console.log('🎯 Page Generation Info:', { ai_generated, token_usage, fallback_used });
        }
      }
    } catch (error) {
      console.error('Error creating wireframe page:', error);
      showNotification({
        message: `Failed to create page: ${error.message}`,
        type: 'error'
      });
    }
  };

  const updatePageSurgically = async (pageId, userRequest) => {
    try {
      setUpdatingPages(prev => new Set([...prev, pageId]));

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages/${pageId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          user_message: userRequest,
          context: { preserve_styling: true, update_mode: 'surgical' }
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update page: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        // Update the page in the list
        setPages(prev => prev.map(page => 
          page.page_id === pageId ? data.data.page : page
        ));

        // Notify parent component of page update
        if (onPageUpdate) {
          onPageUpdate(pageId, data.data.page);
        }

        // Show success with token savings info
        const savings = data.data.token_savings || 0;
        const savingsText = savings > 1000 ? ` (Saved ${Math.round(savings/1000)}k tokens!)` : '';
        
        showNotification({
          message: `Updated ${data.data.page.title} surgically!${savingsText}`,
          type: 'success'
        });

        console.log('🔧 Surgical Update Info:', {
          token_savings: data.data.token_savings,
          tools_used: data.data.tools_used,
          execution_time: data.data.execution_time
        });
      }
    } catch (error) {
      console.error('Error updating page:', error);
      showNotification({
        message: `Failed to update page: ${error.message}`,
        type: 'error'
      });
    } finally {
      setUpdatingPages(prev => {
        const newSet = new Set(prev);
        newSet.delete(pageId);
        return newSet;
      });
    }
  };

  const deletePage = async (pageId, pageTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${pageTitle}"?`)) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages/${pageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to delete page: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setPages(prev => prev.filter(page => page.page_id !== pageId));
        if (expandedPage === pageId) {
          setExpandedPage(null);
        }
        
        showNotification({
          message: `Deleted "${pageTitle}" page`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error deleting page:', error);
      showNotification({
        message: `Failed to delete page: ${error.message}`,
        type: 'error'
      });
    }
  };

  const duplicatePage = async (pageId, currentTitle) => {
    const newPageId = prompt('Enter new page ID:', `${pageId}_copy`);
    const newTitle = prompt('Enter new page title:', `${currentTitle} (Copy)`);
    
    if (!newPageId || !newTitle) return;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8003/api'}/wireframes/projects/${projectId}/pages/${pageId}/duplicate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          new_page_id: newPageId,
          new_title: newTitle
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to duplicate page: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setPages(prev => [...prev, data.data.page]);
        showNotification({
          message: `Duplicated page as "${newTitle}"`,
          type: 'success'
        });
      }
    } catch (error) {
      console.error('Error duplicating page:', error);
      showNotification({
        message: `Failed to duplicate page: ${error.message}`,
        type: 'error'
      });
    }
  };

  const handleAccordionChange = (pageId) => (event, isExpanded) => {
    setExpandedPage(isExpanded ? pageId : null);
  };

  const renderPagePreview = (page) => {
    // Debug page content
    debugPageContent(page);
    
    if (!page.html_content) {
      return (
        <Box sx={{ 
          p: 3, 
          textAlign: 'center', 
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '2px dashed #ddd'
        }}>
          <WebIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
          <Typography variant="body2" color="text.secondary">
            No content generated yet
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Page ID: {page.page_id} | Status: {page.status}
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        border: '1px solid #ddd', 
        borderRadius: 1, 
        overflow: 'hidden',
        mb: 2
      }}>
        <iframe 
          srcDoc={page.html_content}
          title={`${page.title} Preview`}
          style={{ 
            width: '100%', 
            height: '400px', 
            border: 'none',
            background: 'white'
          }}
          sandbox="allow-scripts allow-same-origin"
        />
      </Box>
    );
  };

  const getPageTypeIcon = (pageType) => {
    const type = pageTypes.find(t => t.value === pageType);
    const Icon = type?.icon || WebIcon;
    return <Icon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading wireframe pages...</Typography>
      </Box>
    );
  }

  if (autoGenerating) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 4 }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6" gutterBottom>Auto-generating pages from conversation...</Typography>
        <Typography variant="body2" color="text.secondary">
          Analyzing wireframe content and creating organized pages
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      {/* Header with create button */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        p: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        <Box>
          <Typography variant="h6">Project Wireframes</Typography>
          <Typography variant="body2" color="text.secondary">
            {pages.length > 0 
              ? `${pages.length} page${pages.length !== 1 ? 's' : ''} • Accordion view with surgical editing`
              : 'Auto-generated from conversation content'
            }
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {pages.length === 0 && conversation && (
            <Button
              variant="outlined"
              startIcon={<WebIcon />}
              onClick={autoGenerateFromConversation}
              disabled={autoGenerating}
              size="small"
            >
              Auto-Generate Pages
            </Button>
          )}
          {pages.length > 0 && conversation && (
            <Button
              variant="outlined"
              startIcon={<WebIcon />}
              onClick={autoGenerateFromConversation}
              disabled={autoGenerating}
              size="small"
              color="secondary"
            >
              Refresh Content
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreatePageOpen(true)}
            size="small"
          >
            New Page
          </Button>
          <Button
            variant="text"
            onClick={debugWireframePages}
            size="small"
            color="info"
          >
            🐛 Debug
          </Button>
        </Box>
      </Box>

      {/* No pages state */}
      {pages.length === 0 && !autoGenerating && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <WebIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {conversation ? 'Ready to organize your wireframes' : 'No wireframe pages yet'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {conversation 
              ? 'We can automatically create organized pages from your existing wireframe conversation'
              : 'Create your first wireframe page to get started with the accordion view'
            }
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            {conversation && (
              <Button
                variant="contained"
                startIcon={<WebIcon />}
                onClick={autoGenerateFromConversation}
                disabled={autoGenerating}
              >
                Auto-Generate from Conversation
              </Button>
            )}
            <Button
              variant={conversation ? "outlined" : "contained"}
              startIcon={<AddIcon />}
              onClick={() => setCreatePageOpen(true)}
            >
              Create Manually
            </Button>
          </Box>
        </Paper>
      )}

      {/* Accordion pages */}
      {pages.map((page) => (
        <Accordion
          key={page.page_id}
          expanded={expandedPage === page.page_id}
          onChange={handleAccordionChange(page.page_id)}
          sx={{ mb: 1 }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <Box sx={{ mr: 2, color: 'primary.main' }}>
                {getPageTypeIcon(page.page_type)}
              </Box>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                  {page.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Chip 
                    label={page.page_type} 
                    size="small" 
                    variant="outlined" 
                    sx={{ fontSize: '0.75rem' }}
                  />
                  <Chip 
                    label={page.status} 
                    size="small" 
                    color={page.status === 'complete' ? 'success' : 'default'}
                    sx={{ fontSize: '0.75rem' }}
                  />
                  {page.components && page.components.length > 0 && (
                    <Typography variant="caption" color="text.secondary">
                      {page.components.length} component{page.components.length !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
              </Box>
              
              {/* Page actions */}
              <Box sx={{ display: 'flex', gap: 0.5, mr: 1 }} onClick={(e) => e.stopPropagation()}>
                {updatingPages.has(page.page_id) && (
                  <CircularProgress size={16} sx={{ mr: 1 }} />
                )}
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (showConversation) {
                      showConversation(page.page_id, page.title);
                    }
                  }}
                  title="Edit with AI conversation"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    duplicatePage(page.page_id, page.title);
                  }}
                  title="Duplicate page"
                >
                  <DuplicateIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    deletePage(page.page_id, page.title);
                  }}
                  title="Delete page"
                  color="error"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            </Box>
          </AccordionSummary>
          
          <AccordionDetails>
            <Box sx={{ width: '100%' }}>
              {/* Page info */}
              {page.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {page.description}
                </Typography>
              )}

              {/* Quick surgical update */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Quick Update (Surgical Precision)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={updatingPages.has(page.page_id)}
                    onClick={() => updatePageSurgically(page.page_id, 'Add a search bar to the header')}
                  >
                    + Search Bar
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={updatingPages.has(page.page_id)}
                    onClick={() => updatePageSurgically(page.page_id, 'Change the primary button color to blue')}
                  >
                    Blue Buttons
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={updatingPages.has(page.page_id)}
                    onClick={() => updatePageSurgically(page.page_id, 'Make the layout responsive for mobile devices')}
                  >
                    Mobile Responsive
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    disabled={updatingPages.has(page.page_id)}
                    onClick={() => updatePageSurgically(page.page_id, 'Add a footer with copyright information')}
                  >
                    + Footer
                  </Button>
                </Box>
              </Box>

              {/* Page preview */}
              <Typography variant="subtitle2" gutterBottom>
                Live Preview
              </Typography>
              {renderPagePreview(page)}

              {/* Page stats */}
              {page.structure && (
                <Box sx={{ 
                  mt: 2, 
                  p: 2, 
                  bgcolor: 'background.default',
                  borderRadius: 1,
                  display: 'flex',
                  gap: 3,
                  flexWrap: 'wrap'
                }}>
                  <Typography variant="caption">
                    Elements: {page.structure.elements || 0}
                  </Typography>
                  <Typography variant="caption">
                    Forms: {page.structure.forms || 0}
                  </Typography>
                  <Typography variant="caption">
                    Buttons: {page.structure.buttons || 0}
                  </Typography>
                  <Typography variant="caption">
                    Links: {page.structure.links || 0}
                  </Typography>
                </Box>
              )}
            </Box>
          </AccordionDetails>
        </Accordion>
      ))}

      {/* Create Page Dialog */}
      <Dialog 
        open={createPageOpen} 
        onClose={() => setCreatePageOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Wireframe Page</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Page ID"
              placeholder="e.g., login, dashboard, profile"
              value={newPage.page_id}
              onChange={(e) => setNewPage(prev => ({...prev, page_id: e.target.value}))}
              sx={{ mb: 2 }}
              helperText="Unique identifier for this page (lowercase, no spaces)"
            />
            
            <TextField
              fullWidth
              label="Page Title"
              placeholder="e.g., Login Page, User Dashboard"
              value={newPage.title}
              onChange={(e) => setNewPage(prev => ({...prev, title: e.target.value}))}
              sx={{ mb: 2 }}
            />
            
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Page Type</InputLabel>
              <Select
                value={newPage.page_type}
                label="Page Type"
                onChange={(e) => setNewPage(prev => ({...prev, page_type: e.target.value}))}
              >
                {pageTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <type.icon sx={{ mr: 1, fontSize: 18 }} />
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            <TextField
              fullWidth
              label="Description (Optional)"
              placeholder="Brief description of this page's purpose"
              value={newPage.description}
              onChange={(e) => setNewPage(prev => ({...prev, description: e.target.value}))}
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreatePageOpen(false)}>Cancel</Button>
          <Button 
            variant="contained" 
            onClick={createWireframePage}
            disabled={!newPage.page_id || !newPage.title}
          >
            Create with AI
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WireframeAccordion;